import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Mail, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { LICENSING_PATENTS } from '../data/licensingPatents';

const ORG_TYPES = [
  'Defense / Intelligence Agency or Contractor',
  'Semiconductor Manufacturer (Fab / IDM / OSAT)',
  'Semiconductor Design House / EDA',
  'AI Hyperscaler / Cloud Operator',
  'GPU / Accelerator / Networking Silicon Vendor',
  'Operational Technology (OT/ICS) Vendor',
  'Critical Infrastructure Operator',
  'PKI / Identity / Confidential Computing',
  'Investor / Strategic Buyer',
  'Patent Monetization Firm',
  'University / Research Institution',
  'Other',
];

const ENGAGEMENT_STRUCTURES = [
  'Non-exclusive field-of-use license',
  'Exclusive field-of-use license',
  'Joint development / co-bid (SBIR / STTR / OTA / BAA)',
  'Option agreement / right of first refusal',
  'Portfolio cross-license / bundle',
  'Strategic acquisition discussion',
  'Briefing only — not committed yet',
];

const TIMELINES = [
  'Immediate (within 30 days)',
  'Near-term (1–3 months)',
  'Mid-term (3–6 months)',
  'Strategic (6–12 months)',
  'Exploratory — no specific timeline',
];

const PATENT_CHECKS = [
  ...LICENSING_PATENTS.map((p) => ({ value: p.slug, label: `Patent ${p.num} · ${p.shortTitle}` })),
  { value: 'FullPortfolio', label: 'Full Portfolio (all 7)' },
  { value: 'Unsure',        label: 'Not sure yet — exploring' },
];

const LicensingRequest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', title: '', organization: '', email: '', phone: '',
    orgType: '', patents: [], structure: '', useCase: '', timeline: '',
  });

  /* ?patent=<slug> pre-checks the corresponding box */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preset = params.get('patent');
    if (preset && PATENT_CHECKS.some((p) => p.value === preset)) {
      setForm((prev) => (prev.patents.includes(preset) ? prev : { ...prev, patents: [...prev.patents, preset] }));
    }
  }, [location.search]);

  const togglePatent = (value) => {
    setForm((prev) => ({
      ...prev,
      patents: prev.patents.includes(value)
        ? prev.patents.filter((v) => v !== value)
        : [...prev.patents, value],
    }));
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organization || !form.orgType) {
      toast.error('Please fill in name, organization, email, and organization type.');
      return;
    }
    setSubmitting(true);
    try {
      const messageParts = [
        form.useCase ? `Brief Use Case / Problem Statement:\n${form.useCase}` : null,
        form.patents.length
          ? `Patents of Interest:\n${form.patents.map((v) => '  • ' + (PATENT_CHECKS.find((p) => p.value === v)?.label || v)).join('\n')}`
          : null,
        form.structure ? `Preferred Engagement Structure: ${form.structure}` : null,
        form.timeline ? `Decision Timeline: ${form.timeline}` : null,
        form.title ? `Title / Role: ${form.title}` : null,
      ].filter(Boolean).join('\n\n');

      const resp = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          website: e.target.elements?.website?.value || '',
          email: form.email,
          phone: form.phone,
          organization: form.organization,
          inquiryType: `Licensing / Partnership Inquiry — ${form.orgType}`,
          message: messageParts || '(no additional details provided)',
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || 'Submission failed');
      toast.success('Inquiry sent. We will respond within 2 business days.');
      navigate('/licensing/thank-you');
    } catch (err) {
      toast.error(err.message || 'Could not submit. Please email licensing@salartechnologies.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-950/20">
      <Helmet>
        <title>Request Licensing Brief | SALAR Technologies</title>
        <meta name="description" content="Request the full NDA-only licensing brief for SALAR Technologies' eight pending US patent applications. SALAR responds within two business days with a one-page mutual NDA." />
        <meta property="og:title" content="Request Licensing Brief | SALAR Technologies" />
        <meta property="og:description" content="Request the full NDA-only licensing brief — SALAR responds within two business days." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.salartechnologies.com/licensing/request" />
        <link rel="canonical" href="https://www.salartechnologies.com/licensing/request" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="bg-gradient-to-b from-[#0a1628] via-[#0e1f3a] to-[#0a1628] border-b border-blue-800/40 py-14 lg:py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <Link to="/licensing" className="inline-flex items-center text-sm text-cyan-300 hover:text-cyan-200 mb-6" data-testid="licensing-request-back">
              <ArrowLeft size={14} className="mr-1" /> Back to Patent Portfolio
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
              Request a Licensing Brief
            </h1>
            <p className="text-base lg:text-lg text-blue-100/90 leading-relaxed">
              The full 9–10 page licensing brief is NDA-only and delivered by email within two business days. Tell us
              who you are and which patent(s) you are evaluating — we will return a one-page mutual NDA, then schedule
              a 30-minute briefing call.
            </p>
            <p className="text-sm text-cyan-200/90 mt-4 inline-flex items-center gap-2">
              <ShieldCheck size={14} /> All inquiries are treated as confidential pending NDA.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12" ref={formRef}>
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <Card className="border-blue-700/40 bg-blue-950/40">
              <CardContent className="py-6">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="licensing-request-form">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lr-name" className="text-blue-100">Full Name <span className="text-cyan-300">*</span></Label>
                      <Input id="lr-name" required value={form.name} onChange={handleChange('name')} className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-name" />
                    </div>
                    <div>
                      <Label htmlFor="lr-title" className="text-blue-100">Title / Role</Label>
                      <Input id="lr-title" value={form.title} onChange={handleChange('title')} className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-title" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lr-org" className="text-blue-100">Organization <span className="text-cyan-300">*</span></Label>
                      <Input id="lr-org" required value={form.organization} onChange={handleChange('organization')} className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-organization" />
                    </div>
                    <div>
                      <Label htmlFor="lr-email" className="text-blue-100">Work Email <span className="text-cyan-300">*</span></Label>
                      <Input id="lr-email" type="email" required value={form.email} onChange={handleChange('email')} className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-email" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="lr-phone" className="text-blue-100">Phone</Label>
                      <Input id="lr-phone" type="tel" value={form.phone} onChange={handleChange('phone')} className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-phone" />
                    </div>
                    <div>
                      <Label className="text-blue-100">Organization Type <span className="text-cyan-300">*</span></Label>
                      <Select value={form.orgType || undefined} onValueChange={(v) => v && setForm((prev) => ({ ...prev, orgType: v }))} required>
                        <SelectTrigger className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-orgtype">
                          <SelectValue placeholder="Select one…" />
                        </SelectTrigger>
                        <SelectContent>
                          {ORG_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-blue-100 mb-2 block">Patents of Interest <span className="text-blue-300/60 text-xs">(check all that apply)</span></Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-[#0a1628] border border-blue-700/50 rounded-md">
                      {PATENT_CHECKS.map((p) => (
                        <label key={p.value} className="flex items-start gap-2 text-sm text-blue-100 cursor-pointer hover:text-white">
                          <input
                            type="checkbox"
                            checked={form.patents.includes(p.value)}
                            onChange={() => togglePatent(p.value)}
                            className="mt-1 accent-cyan-500"
                            data-testid={`licensing-request-check-${p.value}`}
                          />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-blue-100">Preferred Engagement Structure</Label>
                    <Select value={form.structure || undefined} onValueChange={(v) => v && setForm((prev) => ({ ...prev, structure: v }))}>
                      <SelectTrigger className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-structure">
                        <SelectValue placeholder="Select one…" />
                      </SelectTrigger>
                      <SelectContent>
                        {ENGAGEMENT_STRUCTURES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="lr-usecase" className="text-blue-100">Brief Use Case</Label>
                    <Textarea
                      id="lr-usecase"
                      value={form.useCase}
                      onChange={handleChange('useCase')}
                      placeholder="Describe the problem, timeline, and any specific requirements (CNSA 2.0, CHIPS Act, deployment scale, etc.)"
                      rows={4}
                      className="bg-[#0a1628] border-blue-700/50 text-white"
                      data-testid="licensing-request-usecase"
                    />
                  </div>

                  <div>
                    <Label className="text-blue-100">Decision Timeline</Label>
                    <Select value={form.timeline || undefined} onValueChange={(v) => v && setForm((prev) => ({ ...prev, timeline: v }))}>
                      <SelectTrigger className="bg-[#0a1628] border-blue-700/50 text-white" data-testid="licensing-request-timeline">
                        <SelectValue placeholder="Select one…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-2">
                    <Button type="submit" disabled={submitting} className="bg-cyan-500 hover:bg-cyan-400 text-[#0a1628] font-bold w-full sm:w-auto px-8 py-6" data-testid="licensing-request-submit">
                      {submitting ? 'Sending…' : (<>Submit Licensing Inquiry <Send className="ml-2" size={16} /></>)}
                    </Button>
                    <p className="text-xs text-blue-200/70 italic mt-3">
                      Your inquiry will be sent directly to <span className="text-cyan-200">licensing@salartechnologies.com</span>.
                      SALAR responds within 2 business days. All inquiries are treated as confidential pending NDA.
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>

            <p className="text-sm text-blue-200/70 mt-6 text-center">
              Prefer email? Write to{' '}
              <a href="mailto:licensing@salartechnologies.com" className="text-cyan-300 hover:text-cyan-200 font-semibold inline-flex items-center gap-1">
                <Mail size={12} /> licensing@salartechnologies.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LicensingRequest;
